// Modal host for the Traffic Signs imagery panels.
//
// These panels used to sit permanently in the wizard's Upload step. Most users
// never run Traffic Signs, so the prerequisite uploads now live behind this
// modal, opened from the TSA task card in the Analyses step.
//
// Follows the task-info modal's markup so it inherits the shared rsa-modal
// styling and the backdrop-close behaviour (which only closes when BOTH the
// press and the release land on the backdrop — a plain onClick would close the
// modal when a user drag-selects text and releases outside it).
import React from 'react';
import ImageryUploadPanels from './ImageryUploadPanels';
import { imageryReadiness } from './useImagerySummaries';
import { backdropCloseHandlers } from '../../../utils/backdropClose';

export default function ImageryUploadModal({
  isOpen,
  onClose,
  projectId,
  sourceStorage,
  onNotify,
  summaries,
  loading,
  fetchErrors,
  refresh,
}) {
  if (!isOpen) return null;

  const { ready, missing, unknown } = imageryReadiness(summaries, fetchErrors);

  return (
    <div
      className="rsa-modal-overlay imagery-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="imagery-modal-title"
      {...backdropCloseHandlers(onClose)}
    >
      <div className="rsa-modal imagery-modal" onClick={(e) => e.stopPropagation()}>
        <div className="task-info-modal-header">
          <h3 id="imagery-modal-title" className="task-info-modal-title">
            Traffic Signs — camera imagery
          </h3>
          <button
            type="button"
            className="task-info-modal-close"
            onClick={onClose}
            aria-label="Close camera imagery"
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>

        <div className="imagery-modal-body">
          <p className="imagery-modal-intro">
            Traffic Signs detects signs in camera frames and places them using the
            LiDAR. Both uploads below are required — a run without them is rejected
            before it starts.
          </p>

          {/* The same condition the backend rejects at dispatch (422
              images_missing / camera_data_missing), surfaced here so the user
              sees it while they can still fix it. */}
          {!ready && (
            <p className={`imagery-modal-banner ${unknown ? 'is-unknown' : 'is-missing'}`}>
              <i
                className={`fas ${unknown ? 'fa-circle-exclamation' : 'fa-triangle-exclamation'}`}
                aria-hidden
              />{' '}
              {unknown
                ? "Couldn't check what's already uploaded — see the details below."
                : `Still needed: ${missing.join(' and ')}.`}
            </p>
          )}

          <ImageryUploadPanels
            projectId={projectId}
            sourceStorage={sourceStorage}
            onNotify={onNotify}
            summaries={summaries}
            loading={loading}
            fetchErrors={fetchErrors}
            refresh={refresh}
          />
        </div>

        <div className="imagery-modal-footer">
          <button
            type="button"
            className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
